import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  PhoneOff,
  Monitor,
  Users,
  Settings,
  ArrowLeft,
  Loader2,
  AlertCircle,
  ExternalLink
} from "lucide-react";

const VideoCall = () => {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const iframeRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roomData, setRoomData] = useState(null);
  const [callActive, setCallActive] = useState(false);

  useEffect(() => {
    if (interviewId) {
      joinVideoRoom();
    }
  }, [interviewId]);

  const joinVideoRoom = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get join token
      const response = await fetch(`${API}/video-calls/join/${interviewId}`, {
        method: "POST",
        credentials: "include",
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to join video room");
      }
      
      const data = await response.json();
      setRoomData(data);
      setCallActive(true);
      
      // Open Daily.co room in iframe with token
      if (iframeRef.current) {
        const roomUrl = `${data.room_url}?t=${data.token}`;
        iframeRef.current.src = roomUrl;
      }
      
    } catch (err) {
      console.error("Failed to join video room:", err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const endCall = async () => {
    try {
      await fetch(`${API}/video-calls/end/${interviewId}`, {
        method: "POST",
        credentials: "include",
      });
      
      toast.success("Call ended");
      navigate(-1);
      
    } catch (err) {
      console.error("Failed to end call:", err);
      toast.error("Failed to end call");
    }
  };

  const leaveCall = () => {
    navigate(-1);
  };

  const dashboardUrl = user?.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/jobseeker";

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-crimson animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Connecting to video room...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Unable to Join Call</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={joinVideoRoom} className="bg-crimson hover:bg-crimson-hover">
              Try Again
            </Button>
            <Link to={dashboardUrl}>
              <Button variant="outline" className="border-[#333] text-white">
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Header */}
      <header className="bg-obsidian border-b border-[#222] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={leaveCall}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-crimson flex items-center justify-center">
              <Video className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-heading text-lg font-bold">LIVE INTERVIEW</span>
              {callActive && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Connected
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {roomData?.is_owner && (
            <Button
              onClick={endCall}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              data-testid="end-call-btn"
            >
              <PhoneOff className="w-4 h-4 mr-2" />
              End Call
            </Button>
          )}
          {!roomData?.is_owner && (
            <Button
              onClick={leaveCall}
              variant="outline"
              className="border-[#333] text-white"
              data-testid="leave-call-btn"
            >
              <PhoneOff className="w-4 h-4 mr-2" />
              Leave
            </Button>
          )}
        </div>
      </header>
      
      {/* Video Container */}
      <div className="flex-1 relative">
        <iframe
          ref={iframeRef}
          className="w-full h-full"
          style={{ minHeight: "calc(100vh - 64px)" }}
          allow="camera; microphone; fullscreen; display-capture; autoplay"
          data-testid="video-iframe"
        />
      </div>
    </div>
  );
};

export default VideoCall;
